import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Column } from "@/shared/ui/Blocks";
import LeadershipSpanDifferentiator from "./LeadershipSpanDifferentiator";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { LSpanKey } from "@/lib/leadership-span-bucket-score-parser";
import { IconButton, SectionHeader } from "@/shared/ui";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalName } from "@/shared/modals";

export interface LeadershipSpanWidgetProps {
  leadershipSpan: DimensionWithScores[];
  candidateName?: string;
  className?: string;
}

// All eight differentiators that should be displayed
export const LEADERSHIP_SPAN_DIFFERENTIATORS: LSpanKey[] = [
  "disruptive",
  "pragmatic",
  "risk_taking",
  "measured",
  "heroic",
  "vulnerable",
  "galvanizing",
  "connecting",
];

function LeadershipSpanWidget({
  leadershipSpan,
  className,
}: LeadershipSpanWidgetProps) {
  const { t } = useTranslation();

  const infoModalContent = [
    {
      title: "Interpretation",
      text: "Results are informed by the executive’s performance on the online assessments and any additional information that was gathered. These scores represent an executive’s fit on each of the eight Leadership Span™ differentiators and fall into Low, Moderate, and High categories. For a more complete understanding of this individual’s Leadership Span™ capabilities, RRA recommends building on these results with a competency-based interview.",
    },
    {
      title: "Background",
      text: "Through a series of studies on effective leadership, Russell Reynolds Associates (RRA) determined that the best executives are defined less by a set of single competencies and more by their ability to span critical competencies in the face of changing circumstances. Built in partnership with Hogan Assessment Systems, our model captures an executive’s tendencies and approach toward balancing the natural tensions leaders navigate every day.",
    },
  ];

  const [openInfoModal] = useModal(ModalName.INFO, {
    contentSections: infoModalContent,
    title: t("leadershipSpan.title"),
  });

  const differentiatorsData = useMemo(() => {
    return LEADERSHIP_SPAN_DIFFERENTIATORS.map((key) => {
      const dimension = leadershipSpan.find((dim) => dim.code === key);
      return {
        key,
        dimension: dimension || null,
      };
    }).filter((item) => item.dimension !== null);
  }, [leadershipSpan]);

  return (
    <Column className={className}>
      <SectionHeader
        title={t("leadershipSpan.title")}
        subtitle={t("leadershipSpan.subtitle")}
        RightComponent={
          <IconButton
            variant="ghost"
            icon="Info"
            color="grey"
            onClick={() => {
              openInfoModal();
            }}
          />
        }
      />

      <Column className="space-y-0">
        {differentiatorsData.map(({ key, dimension }) => {
          if (!dimension) return null;

          return (
            <LeadershipSpanDifferentiator
              key={key}
              dimension={dimension}
              dimensionKey={key}
            />
          );
        })}
      </Column>
    </Column>
  );
}

export default LeadershipSpanWidget;
